import React from "react";
import {
  Box,
  Button,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import { CURRENCIES, type FXSpotAccordionProps } from "./types";

export const FXSpotAccordion: React.FC<FXSpotAccordionProps> = ({
  expanded,
  onAccordionChange,
  fxRows,
  clients,
  custodians,
  handleFXChange,
  handleAddFXRow,
  handleRemoveFXRow,
  handleReAddFXRow,
}) => {
  return (
    <Accordion
      expanded={expanded}
      onChange={onAccordionChange}
      sx={{ mb: 1 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <CurrencyExchangeIcon color="primary" />
          <Typography fontWeight={500}>Add FX Spot</Typography>
          <Typography variant="body2" color="text.secondary">
            (Foreign Exchange)
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Client</TableCell>
                <TableCell>Custodian</TableCell>
                <TableCell>Buy Amount</TableCell>
                <TableCell>Buy Currency</TableCell>
                <TableCell>Sell Currency</TableCell>
                <TableCell width={100}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fxRows.map((row, index) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      value={row.client_id}
                      onChange={(e) => handleFXChange(row.id, "client_id", e.target.value)}
                      variant="standard"
                      sx={{ minWidth: 150 }}
                    >
                      <MenuItem value="">Select...</MenuItem>
                      {clients.map((c) => (
                        <MenuItem key={c.client_id} value={c.client_id}>
                          {c.client_name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      value={row.custodian_id}
                      onChange={(e) => handleFXChange(row.id, "custodian_id", e.target.value)}
                      variant="standard"
                      sx={{ minWidth: 120 }}
                    >
                      <MenuItem value="">Select...</MenuItem>
                      {custodians.map((c) => (
                        <MenuItem key={c.custodian_id} value={c.custodian_id}>
                          {c.custodian_name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={row.buy_amount || ""}
                      onChange={(e) => handleFXChange(row.id, "buy_amount", parseFloat(e.target.value) || 0)}
                      variant="standard"
                      sx={{ width: 120 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      value={row.buy_currency}
                      onChange={(e) => handleFXChange(row.id, "buy_currency", e.target.value)}
                      variant="standard"
                      sx={{ width: 100 }}
                    >
                      {CURRENCIES.map((c) => (
                        <MenuItem key={c} value={c}>{c}</MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      value={row.sell_currency}
                      onChange={(e) => handleFXChange(row.id, "sell_currency", e.target.value)}
                      variant="standard"
                      sx={{ width: 100 }}
                    >
                      {CURRENCIES.map((c) => (
                        <MenuItem key={c} value={c}>{c}</MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                      {index === fxRows.length - 1 ? (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleAddFXRow}
                          sx={{ minWidth: 60 }}
                        >
                          ADD
                        </Button>
                      ) : (
                        <>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleReAddFXRow(row)}
                            sx={{ minWidth: 60 }}
                          >
                            ADD
                          </Button>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveFXRow(row.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
};
